import { ComponentProps } from 'react';

import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Alert, AlertProps } from '@sola/ark-core/molecules/alert/alert.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import i18n from '@sola/locales/index.ts';

import { LocalAlertConfig } from '../../types/localeAlerts.ts';
import { useLocalAlerts } from './local-alerts-provider.tsx';

/**
 * Single local alert.
 */
export const LocalAlert = ({
  alert,
  onClose,
  className,
  scrollIntoView = true,
  scrollPosition = 'start',
}: {
  alert: LocalAlertConfig;
  onClose?: (id: string) => void;
  className?: string;
  scrollIntoView?: AlertProps['scrollIntoView'];
  scrollPosition?: AlertProps['scrollPosition'];
}) => {
  const { translationKey, translationData, id, ...alertProps } = alert;
  const hasTitle = i18n.exists(`my:${translationKey}.title`);
  let hasMessage = i18n.exists(`my:${translationKey}.message`);
  let translation = hasMessage ? (
    <IntlBlock
      i18nKey={`my:${translationKey}.message`}
      values={translationData}
    />
  ) : null;

  if (!hasMessage && !hasTitle && i18n.exists(`my:${translationKey}`)) {
    hasMessage = true;
    translation = (
      <IntlBlock i18nKey={`my:${translationKey}`} values={translationData} />
    );
  }

  const title = hasTitle ? (
    <IntlBlock
      i18nKey={`my:${translationKey}.title`}
      values={translationData}
      tOptions={{
        interpolation: {
          escapeValue: false,
        },
      }}
    />
  ) : null;

  return (
    (hasMessage || hasTitle) && (
      <Alert
        title={title}
        message={translation}
        scrollIntoView={scrollIntoView}
        scrollPosition={scrollPosition}
        closable
        onClose={onClose ? () => onClose(id) : undefined}
        className={cn('scroll-mt-roomy-md', className)}
        {...alertProps}
      />
    )
  );
};

/**
 * Component that renders all local alerts.
 */
export const LocalAlerts: React.FC<
  Pick<ComponentProps<'div'>, 'className' | 'style'> & {
    scrollPosition?: AlertProps['scrollPosition'];
  }
> = ({ className, style, scrollPosition }) => {
  const { alerts, removeAlert } = useLocalAlerts();

  if (!alerts?.length) {
    return null;
  }

  return (
    <div className={className} style={style}>
      {alerts.map((alert) => {
        return (
          <LocalAlert
            alert={alert}
            onClose={removeAlert}
            key={alert.id}
            scrollPosition={scrollPosition}
          />
        );
      })}
    </div>
  );
};
